/**
 * Helpers shared by the CustomAliyah, CustomHaftarah, and CustomText views.
 */
import * as yup from 'yup'

import { ROOT_USERNAME } from '@/constants/appConstants'

/** Optional year field: blank or NaN becomes null; defaults to the current year. */
export const yearFieldSchema = yup
  .number()
  .nullable()
  .transform((value) => (isNaN(value) ? null : value))
  .default(() => new Date().getFullYear())

/**
 * The current user's items (sorted), followed by the system (root) user's items (sorted) when includeRoot is set.
 */
export function sortUserThenRoot<T>(
  items: readonly T[],
  options: {
    compare: (a: T, b: T) => number
    currentUsername: string
    includeRoot: boolean
    usernameOf: (item: T) => string
  },
): T[] {
  const sortedFor = (username: string) => items.filter((item) => options.usernameOf(item) === username).sort(options.compare)

  return sortedFor(options.currentUsername).concat(options.includeRoot ? sortedFor(ROOT_USERNAME) : [])
}

/**
 * Resolve the row item from an edit handler's arguments. The data-table's `click:row` passes `(event, { item })`
 * while the pencil button passes the item itself; later arguments win.
 */
export function extractRowItem<T>(isItem: (candidate: unknown) => candidate is T, ...args: unknown[]): T | null {
  let found: T | null = null
  for (const arg of args) {
    const nested = (arg as { item?: unknown } | null | undefined)?.item
    if (isItem(arg)) {
      found = arg
    }
    if (isItem(nested)) {
      found = nested
    }
  }
  return found
}
